import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

const ALL_SUGGESTIONS = [
  "🏡 I'm buying my first home. What insurance should I consider?",
  "👶 We've just had a baby. What should we be thinking about?",
  "💼 I'm self employed. How can I protect my income?",
  "❤️ How can I make sure my family is financially protected?",
  "💰 How much life insurance do I actually need?",
  "🤔 What's the difference between Life, Trauma and Income Protection?",
  "🩺 Is private health insurance worth it in New Zealand?",
  "📋 I already have insurance. How do I know if it's enough?",
];

// Rotate the starters so returning visitors see fresh prompts each visit.
function rotatingSuggestions(count = 4) {
  let offset = 0;
  try {
    offset = parseInt(localStorage.getItem("qg_rot") || "0", 10) || 0;
    localStorage.setItem("qg_rot", String((offset + count) % ALL_SUGGESTIONS.length));
  } catch {}
  return Array.from({ length: count }, (_, i) => ALL_SUGGESTIONS[(offset + i) % ALL_SUGGESTIONS.length]);
}

const WELCOME = "Kia ora! I'm Quantum Guide.\n\nI'm here to help you understand insurance in simple, everyday language. Whether you're buying your first home, starting a family, changing jobs, reviewing your cover, or simply have a question, I'll help explain your options without the jargon.\n\nPlease note: I provide educational information only and not personalised financial advice.";

const genId = () => "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);

export const QuantumGuide = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genId);
  const [suggestions] = useState(() => rotatingSuggestions(4));
  const [messages, setMessages] = useState([
    { role: "assistant", content: WELCOME },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't connect just now. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="quantum-guide-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full bg-[#0B3D2E] text-white grid place-items-center shadow-[0_10px_40px_rgba(10,25,47,0.4)] hover:scale-105 transition-transform"
        aria-label="Open Quantum Guide"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} className="text-emerald-400" />}
      </button>

      {open && (
        <div
          data-testid="quantum-guide-panel"
          className="fixed bottom-24 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[400px] h-[560px] max-h-[calc(100vh-8rem)] bg-white rounded-3xl shadow-[0_20px_70px_rgba(0,0,0,0.25)] border border-slate-200 flex flex-col overflow-hidden qc-fade-up"
        >
          <div className="bg-[#0B3D2E] px-5 py-4 flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-emerald-500/20 text-emerald-400">
              <Sparkles size={18} />
            </span>
            <div>
              <p className="text-white font-display font-medium leading-tight">Quantum Guide</p>
              <p className="text-emerald-400/80 text-xs">Simple answers. No jargon.</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-[#0B3D2E] text-white rounded-br-md" : "bg-white text-slate-700 border border-slate-200 rounded-bl-md"
                  }`}
                >
                  {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            {messages.length <= 1 && (
              <div className="pt-2 space-y-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    data-testid="chat-suggestion"
                    onClick={() => send(s)}
                    className="block w-full text-left text-sm px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-600 hover:border-emerald-300 hover:text-emerald-700 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-slate-200 bg-white">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask anything…"
                className="flex-1 rounded-full bg-slate-100 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <button
                data-testid="chat-send"
                type="submit"
                disabled={busy}
                className="grid place-items-center h-10 w-10 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                <Send size={17} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
